import { Node } from 'reactflow'

import { NodeType } from '@ui/__generated__/graphql'
import { SourceHandleEnum, TargetHandleEnum } from '@ui/interfaces'
import { uuid } from '@ui/lib/utils'
import { useNodeAndEdgeStore } from '@ui/stores'

import { createEdge } from './createEdge'
import { createNode } from './createNode'

interface CreatePlaceholderNode {
  placeHolderNodeId?: string
  currentNodeId: string
  defaultCode?: string
  undeletable?: boolean
  init?: boolean
}

export const createPlaceholderNode = async ({
  placeHolderNodeId = uuid(),
  currentNodeId,
  undeletable = true,
  init = false,
}: CreatePlaceholderNode): Promise<Node> => {
  const nodes = !init ? useNodeAndEdgeStore.getState().nodes : []
  const edges = !init ? useNodeAndEdgeStore.getState().edges : []
  const setNodes = !init ? useNodeAndEdgeStore.getState().setNodes : () => {}
  const setEdges = !init ? useNodeAndEdgeStore.getState().setEdges : () => {}

  // Should create placeholder node only if there is no child node
  // and the node is not draggable
  const childEdges = edges.filter((edge) => edge.source === currentNodeId)
  const childNode = nodes.find((node) => childEdges.map((edge) => edge.target).includes(node.id))

  if (childNode) {
    setEdges((edges) => {
      return edges.map((edge) => {
        if (edge.target === currentNodeId) {
          return { ...edge, animated: false }
        }

        if (edge.source === currentNodeId) {
          return { ...edge, animated: false }
        }
        return edge
      })
    })
    return childNode
  }

  const newPlaceholderNode = createNode({
    nodeId: placeHolderNodeId,
    nodeType: NodeType.Placeholder,
    parentNodeId: currentNodeId,
    undeletable,
  })

  const logEdge = createEdge({
    source: currentNodeId,
    target: placeHolderNodeId,
    targetHandle: TargetHandleEnum.TARGET_LOGS,
    sourceHandle: SourceHandleEnum.SOURCE_LOGS,
    placeholder: true,
  })

  const spanEdge = createEdge({
    source: currentNodeId,
    target: placeHolderNodeId,
    targetHandle: TargetHandleEnum.TARGET_SPANS,
    sourceHandle: SourceHandleEnum.SOURCE_SPANS,
    placeholder: true,
  })

  const metricsEdge = createEdge({
    source: currentNodeId,
    target: placeHolderNodeId,
    targetHandle: TargetHandleEnum.TARGET_METRICS,
    sourceHandle: SourceHandleEnum.SOURCE_METRICS,
    placeholder: true,
  })

  setNodes((nodes) => {
    return nodes.concat([newPlaceholderNode])
  })

  setEdges((edges) => {
    return edges
      .map((edge) => {
        if (edge.target === currentNodeId) {
          return { ...edge, animated: false }
        }

        return edge
      })
      .concat([logEdge, spanEdge, metricsEdge])
  })

  return newPlaceholderNode
}
